import { FixedDistributionAccount, FixedDistributionDateBehavior, } from '@debbie/common/dist/economy/fixed-distributions/index.js';
import { ceilingBasis } from '@debbie/common/dist/economy/helpers/engine-v2/ceiling.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { getCostCeilingSummary } from '@debbie/common/dist/economy/helpers/interest/index.js';
import { isEngineV2 } from '@debbie/common/dist/economy/helpers/policy.js';
import { clone } from '@debbie/common/dist/economy/helpers/utils.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useLimitationDocuments } from '@debbie/cortex/dist/documents/index.js';
import { useEconomyPolicy } from '@debbie/cortex/dist/economy-policy/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { useInterestCalulation } from '@debbie/cortex/dist/heavy-calculations/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import THEME from '../../../theme.js';
import Badge from '../../Badge.js';
import { AlertTriangle } from '../../Icons/index.js';
// What corrections take from each voucher, as engine 2 sets its ceiling.
const corrections = (fixedDistributions) => {
    const corrected = new Map();
    for (const fd of fixedDistributions) {
        if (fd.dateBehavior === FixedDistributionDateBehavior.FromOriginal &&
            fd.account !== FixedDistributionAccount.Creditor) {
            for (const id of [fd.fromCaseVoucherId, fd.toCaseVoucherId]) {
                corrected.set(id, (corrected.get(id) ?? 0) + fd.amount);
            }
        }
    }
    return corrected;
};
const CostCeilingBanner = (props) => {
    const { singleCase } = props;
    const lang = useSelector((s) => s.lang);
    const conditionsLoader = useCaseConditions(singleCase.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const activeCeiling = conditions?.costCeiling.enabled
        ? conditions.costCeiling
        : null;
    const caseVouchersLoader = useCaseVouchersByCase(singleCase.caseId);
    const caseVouchers = h.hasData(caseVouchersLoader)
        ? caseVouchersLoader.data
        : null;
    const documentsLoader = useLimitationDocuments(singleCase.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const economyPolicy = useEconomyPolicy(singleCase);
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(singleCase.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const ceilingCaseVouchers = useMemo(() => caseVouchers != null && activeCeiling != null
        ? clone(helpers.vouchers.postProcessing(caseVouchers, {
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
            includeIrrecoverableVouchers: false,
        }))
        : null, [caseVouchers, activeCeiling]);
    const interestLoader = useInterestCalulation(ceilingCaseVouchers, documents, economyPolicy, fixedDistributions, activeCeiling != null ? conditions : null, moment().startOf('day').format(), {});
    const exceeded = useMemo(() => {
        if (activeCeiling == null ||
            ceilingCaseVouchers == null ||
            economyPolicy == null ||
            fixedDistributions == null) {
            return false;
        }
        // Engine 2 sets its ceiling from debt as entered, not what expired.
        const basis = isEngineV2(economyPolicy)
            ? ceilingBasis(ceilingCaseVouchers, corrections(fixedDistributions))
            : ceilingCaseVouchers;
        return (getCostCeilingSummary(basis, activeCeiling)?.exceededByStaticCosts ===
            true);
    }, [ceilingCaseVouchers, activeCeiling, economyPolicy, fixedDistributions]);
    const reached = h.hasData(interestLoader) &&
        interestLoader.data.some((v) => v.costCeilingApplied);
    if (!exceeded && !reached) {
        return null;
    }
    return (React.createElement(Badge, { theme: exceeded ? THEME.DANGEROUS : THEME.WARNING, title: exceeded
            ? lang.economy.costCeilingExceededDescription
            : lang.economy.costCeilingReachedDescription, style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3em',
            marginBottom: 0,
        } },
        React.createElement(AlertTriangle, { style: { fontSize: '1.1em' } }),
        exceeded
            ? lang.economy.costCeilingExceeded
            : lang.economy.costCeilingReached));
};
export default CostCeilingBanner;
//# sourceMappingURL=CostCeilingBanner.js.map